import { FunctionComponent, ReactNode } from "react";
import JobWorkFields from "./JobWorkFields";
import LineItemActions from "./LineItemActions";
import usePOLineItem from "./usePOLineItem";
import { POLineItemCardProps } from "./types";
import ItemSelectField from "../../../../../ui/new_components/entity_selects/ItemSelectField";
import ProjectSelectField from "../../../../../ui/new_components/entity_selects/ProjectSelectField";
import { formatAmountWithCommas } from "../../../../../ui/pages/content_panel/utils/helpers";

const Row: FunctionComponent<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <span className="text-gray-400">{label}</span> :{" "}
    <span className="font-bold break-words">{children}</span>
  </div>
);

const POLineItemCardMobile: FunctionComponent<POLineItemCardProps> = (
  props,
) => {
  const {
    data,
    editable,
    setEditable,
    readOnly,
    isAddRow,
    isJobWork,
    canAdd,
    defaultProject,
    tax,
    itemSearchDisabled,
    qtyDisabled,
    itemWac,
    showWacWarning,
    onDone,
    cancelEdit,
    remove,
    handleItemSelect,
    handleProjectSelection,
    onQtyChange,
    onPriceChange,
    onLineTypeChange,
    onDcSelect,
  } = usePOLineItem(props);

  const canEditFields = editable && !readOnly;
  const symbol = props.currency?.symbol ?? "";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onDone();
      }}
      className="bg-white text-sm"
    >
      <div className="border rounded-lg shadow-sm p-3 text-xs flex flex-col gap-1.5">
        <Row label="Item Code">{data?.item?.item_code ?? "--"}</Row>
        <div className="flex flex-col gap-1">
          <span className="text-gray-400">Item Name</span>
          {canEditFields ? (
            <ItemSelectField
              disabled={itemSearchDisabled}
              value={data.item}
              onSelect={handleItemSelect}
            />
          ) : (
            <span className="font-bold">{data?.item?.name ?? "--"}</span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-gray-400">Description</span>
          <span className="font-bold break-words">
            {data?.item?.description ?? "--"}
          </span>
        </div>

        {isJobWork && (
          <JobWorkFields
            editable={editable}
            readOnly={readOnly}
            lineType={data.line_type}
            dcReferenceId={data.dc_reference_id}
            onLineTypeChange={onLineTypeChange}
            onDcSelect={onDcSelect}
          />
        )}

        <div className="flex flex-col gap-1">
          <span className="text-gray-400">Project Name</span>
          {canEditFields ? (
            <ProjectSelectField
              disabled={readOnly}
              value={data?.project ?? defaultProject ?? null}
              onSelect={handleProjectSelection}
              placeholder="Select a Project"
            />
          ) : (
            <span className="font-bold">
              {data?.project?.name ?? defaultProject?.name ?? "--"}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
          <Row label="HSN/SAC">{data?.item?.tax_detail?.hsn_sac || "--"}</Row>
          <Row label="Unit">{data?.item?.unit?.formal_name || "--"}</Row>
          <div className="flex items-center gap-1">
            <span className="text-gray-400">Qty</span> :{" "}
            {canEditFields ? (
              <input
                value={data.unit_no ?? 0}
                disabled={qtyDisabled}
                type="number"
                className="text-center p-0.5 border rounded w-16"
                onChange={onQtyChange}
              />
            ) : (
              <span className="font-bold">{data.unit_no ?? 0}</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-400">Price</span> :{" "}
            {canEditFields ? (
              <input
                value={data.price ?? 0}
                type="number"
                className="text-center p-0.5 border rounded w-20"
                onChange={onPriceChange}
              />
            ) : (
              <span className="font-bold">{data.price ?? 0}</span>
            )}
          </div>
          {canEditFields && showWacWarning && (
            <div className="col-span-2 text-red-600">
              The usual price for this item is {symbol}{" "}
              {formatAmountWithCommas(itemWac!)}. Are you sure?
            </div>
          )}
          <Row label="CGST">{tax.cgst}</Row>
          <Row label="SGST">{tax.sgst}</Row>
          <Row label="IGST">{tax.igst}</Row>
          <Row label="Amount">{tax.amount}</Row>
        </div>

        {!readOnly && (
          <div className="flex flex-wrap gap-2 items-center justify-end mt-1 border-t pt-2">
            <LineItemActions
              isAddRow={isAddRow}
              editable={editable}
              canAdd={canAdd}
              onEdit={() => setEditable(true)}
              onSave={() => {
                onDone();
                setEditable(false);
              }}
              onCancel={cancelEdit}
              onDelete={remove}
            />
          </div>
        )}
      </div>
    </form>
  );
};

export default POLineItemCardMobile;
